import logUtil from './log-util'
import axios from './axios-util'
import {
  recordCplStart,
  recordCplEnd,
} from '../components/common/analytics/utils'
const logger = logUtil

const apiFactory = {}

const endCPL = ({ url, event, status, log, err, payload }) => {
  if (err) {
    logger.error(`${log} failed.`, payload)
  } else {
    logger.info(`${log} completed.`, payload)
  }
  recordCplEnd(url, {
    cpl_event: event ? event : 'api load',
    status: status ? status : 500,
  })
}

const startCPL = ({ url, log, payload }) => {
  logger.info(`${log} starting.`, payload)
  recordCplStart(url)
}

apiFactory.loadAllAppData = async ({ queryString }) => {
  let allAppData
  try {
    startCPL({ url: '/developer/api/applications', log: 'Loading apps' })

    let response = await axios.get(`/dashboard/api/applications${queryString}`)
    allAppData = response?.data
    endCPL({
      url: '/developer/api/applications',
      log: 'Loading apps',
      status: response.status,
    })
  } catch (err) {
    endCPL({
      url: '/developer/api/applications',
      log: 'Loading apps',
      status: 500,
      err,
    })

    throw err
  }
  return allAppData
}

apiFactory.loadAllAccount = async () => {
  let allAccountData
  try {
    startCPL({ url: '/developer/api/accounts', log: 'Loading all accounts' })
    const response = await axios.get(`/developer/api/accounts`)

    allAccountData = response?.data
    endCPL({
      url: '/developer/api/accounts',
      log: 'Loading all accounts',
      status: response.status,
    })
  } catch (err) {
    endCPL({
      url: '/developer/api/accounts',
      log: 'Loading all accounts',
      status: 500,
      err,
    })
  }
  return allAccountData
}

apiFactory.loadAccountDetails = async ({ accountId }) => {
  let accountApiData
  if (!accountId) return accountApiData
  startCPL({
    url: '/developer/api/accounts/detail',
    log: 'Loading account details',
  })
  try {
    const response = await axios.get(
      `/developer/api/accounts/detail/${accountId}`,
      { headers: { 'Content-Type': 'application/json' } },
    )
    accountApiData = response.data
    endCPL({
      url: '/developer/api/accounts/detail',
      log: 'Loading account details',
      status: response.status,
    })
  } catch (err) {
    endCPL({
      url: '/developer/api/accounts/detail',
      log: 'Loading account details',
      status: 500,
    })
  }
  return accountApiData
}

apiFactory.loadAppsCredentialUsingClientId = async ({ clientId }) => {
  let appsCredentials = []
  if (!clientId) return appsCredentials
  try {
    startCPL({
      url: '/developer/api/applications/edit/',
      log: 'loadAppsCredentialUsingClientId',
    })
    const configs = {
      headers: { 'Content-Type': 'application/json' },
      params: {
        appname: clientId,
        limit: 'apps',
      },
    }
    const response = await axios.get(
      `/developer/api/applications/edit/${clientId}`,
      configs,
    )
    if (!response) {
      logger.log(
        'There are no app credentials returned from the server using encrypted client id.',
      )
    } else if (response?.data?.app?.credentials) {
      appsCredentials = response?.data?.app
    }
    endCPL({
      url: '/developer/api/applications/edit/',
      log: 'loadAppsCredentialUsingClientId',
      status: response.status,
    })
  } catch (err) {
    endCPL({
      url: '/developer/api/applications/edit/',
      log: 'loadAppsCredentialUsingClientId',
      status: 500,
      err,
    })
  }
  return appsCredentials
}

apiFactory.loadCreditCardDetails = async ({ csrf }) => {
  let cardApiData
  try {
    startCPL({
      url: '/developer/api/creditCardGenerator',
      log: 'Loading CreditCardDetails',
    })
    const response = await axios({
      url: '/developer/api/creditCardGenerator',
      method: 'post',
      headers: {
        'Content-Type': 'application/json',
        'x-csrf-token': csrf,
      },
    })

    cardApiData = response?.data
    endCPL({
      url: '/developer/api/creditCardGenerator',
      log: 'Loading CreditCardDetails',
      status: response.status,
    })
  } catch (err) {
    endCPL({
      url: '/developer/api/creditCardGenerator',
      log: 'Loading CreditCardDetails',
      status: 500,
      err,
    })
  }
  return cardApiData
}

apiFactory.loadAppsCredentialUsingAccountNumber = async ({ csrf }) => {
  let appsCredentials = []
  try {
    startCPL({
      url: '/developer/api/dashboard/live',
      log: 'Loading AppsCredentialUsingAccountNumber',
    })
    const configs = {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'x-csrf-token': csrf,
      },
      params: { limit: 'apps' },
      responseType: 'json',
      contentType: 'application/json',
    }
    appsCredentials = await axios.get(`/developer/api/dashboard/live`, configs)

    endCPL({
      url: '/developer/api/dashboard/live',
      log: 'Loading AppsCredentialUsingAccountNumber',
      status: appsCredentials.status,
    })
  } catch (err) {
    endCPL({
      url: '/developer/api/dashboard/live',
      log: 'Loading AppsCredentialUsingAccountNumber',
      status: 500,
      err,
    })
  }
  return appsCredentials
}

apiFactory.loadRecentTransactions = async ({ csrf, targetUrl, urlParam }) => {
  let transactionApiData
  try {
    startCPL({ url: targetUrl, log: 'Loading RecentTransactions' })
    const configs = {
      params: urlParam,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'x-csrf-token': csrf,
      },
      responseType: 'json',
      contentType: 'application/json',
    }

    transactionApiData = await axios.get(targetUrl, configs)
    endCPL({
      url: targetUrl,
      log: 'Loading RecentTransactions',
      status: transactionApiData.status,
    })
  } catch (err) {
    endCPL({
      url: targetUrl,
      log: 'Loading RecentTransactions',
      status: 500,
      err,
    })
  }
  return transactionApiData
}

apiFactory.loadAPIErrorDetails = async ({
  csrf,
  targetUrl,
  apiCallsUrlParam,
  environment,
}) => {
  let apiErrorData = []
  try {
    startCPL({ url: targetUrl, log: 'Loading APIErrorDetails' })

    const configs = {
      params: apiCallsUrlParam,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'x-csrf-token': csrf,
      },
      responseType: 'json',
      contentType: 'application/json',
    }
    const data = await axios.get(targetUrl, configs)
    apiErrorData = environment === 'sandbox' ? data?.data : data?.data?.data

    endCPL({
      url: targetUrl,
      log: 'Loading APIErrorDetails',
      status: data.status,
    })
  } catch (err) {
    endCPL({
      url: targetUrl,
      log: 'Loading APIErrorDetails',
      status: 500,
      err,
    })
  }
  return apiErrorData
}

apiFactory.loadWebhookErrorDetails = async ({ csrf, targetUrl, params }) => {
  let webhooksErrorData = []
  try {
    startCPL({ url: targetUrl, log: 'Loading WebhookErrorDetails' })
    const configs = {
      params,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'x-csrf-token': csrf,
      },
      responseType: 'json',
      contentType: 'application/json',
    }

    webhooksErrorData = await axios.get(targetUrl, configs)
    logger.log('loadWebhookErrorDetails completed')

    endCPL({
      url: targetUrl,
      log: 'Loading WebhookErrorDetails',
      status: webhooksErrorData.status,
    })
  } catch (err) {
    endCPL({
      url: targetUrl,
      log: 'Loading WebhookErrorDetails',
      status: 500,
      err,
    })
  }
  return webhooksErrorData
}

apiFactory.loadAPIWebhookErrorDetail = async ({ csrf, targetUrl, params }) => {
  let apiWebhooksErrorData = []
  try {
    startCPL({ url: targetUrl, log: 'Loading APIWebhookErrorDetails' })
    const configs = {
      params,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'x-csrf-token': csrf,
      },
      responseType: 'json',
      contentType: 'application/json',
    }

    apiWebhooksErrorData = await axios.get(targetUrl, configs)

    endCPL({
      url: targetUrl,
      log: 'Loading APIWebhookErrorDetails',
      status: apiWebhooksErrorData.status,
    })
  } catch (err) {
    endCPL({
      url: targetUrl,
      log: 'Loading APIWebhookErrorDetails',
      status: 500,
      err,
    })
  }
  return apiWebhooksErrorData
}
apiFactory.loadAPIWebhookAttemptHistory = async ({ csrf, url, params }) => {
  let apiWebhooksErrorData = []
  try {
    startCPL({ url, log: 'Loading loadAPIWebhookAttemptHistory' })
    const configs = {
      params,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'x-csrf-token': csrf,
      },
      responseType: 'json',
      contentType: 'application/json',
    }

    apiWebhooksErrorData = await axios.get(url, configs)

    endCPL({
      url,
      log: 'Loading loadAPIWebhookAttemptHistory',
      status: apiWebhooksErrorData.status,
    })
  } catch (err) {
    endCPL({
      url,
      log: 'Loading loadAPIWebhookAttemptHistory',
      status: 500,
      err,
    })
  }
  return apiWebhooksErrorData
}

apiFactory.loadSandboxAppData = async ({ csrf }) => {
  let sbAppData
  try {
    startCPL({
      url: `/dashboard/api/resthistory/sandbox`,
      log: 'Loading sandbox apps',
    })
    const configs = {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'x-csrf-token': csrf,
      },
      responseType: 'json',
      contentType: 'application/json',
    }
    const response = await axios.get(
      `/dashboard/api/resthistory/sandbox`,
      configs,
    )
    sbAppData = response?.data
    endCPL({
      url: `/dashboard/api/resthistory/sandbox`,
      log: 'Loading sandbox apps',
      status: response.status,
    })
  } catch (err) {
    endCPL({
      url: `/dashboard/api/resthistory/sandbox`,
      log: 'Loading sandbox apps',
      status: 500,
      err,
    })

    throw err
  }
  return sbAppData
}

apiFactory.loadAPICallsDetail = async ({ csrf, params }) => {
  let apiWebhooksErrorData = []
  try {
    startCPL({
      url: '/dashboard/api/resthistory/detail',
      log: 'Loading APICallsDetail',
    })
    const configs = {
      params,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'x-csrf-token': csrf,
      },
      responseType: 'json',
      contentType: 'application/json',
    }

    apiWebhooksErrorData = await axios.get(
      '/dashboard/api/resthistory/detail',
      configs,
    )

    endCPL({
      url: '/dashboard/api/resthistory/detail',
      log: 'Loading APICallsDetail',
      status: apiWebhooksErrorData.status,
    })
  } catch (err) {
    endCPL({
      url: '/dashboard/api/resthistory/detail',
      log: 'Loading APICallsDetail',
      status: 500,
      err,
    })
  }
  return apiWebhooksErrorData
}

apiFactory.getUserPreferences = async () => {
  try {
    startCPL({
      url: '/dashboard/userPreferences',
      log: 'getUserPreferences',
    })
    const response = await axios.get(`/dashboard/userPreferences`)

    endCPL({
      url: '/dashboard/userPreferences',
      log: 'getUserPreferences',
      status: response.status,
    })
    return response?.data ? response?.data : []
  } catch (error) {
    endCPL({
      url: '/dashboard/userPreferences',
      log: 'getUserPreferences',
      status: error.response.status,
      error,
    })
    return []
  }
}

apiFactory.addUserPreference = async (csrf, preference) => {
  try {
    startCPL({
      url: '/dashboard/userPreferences',
      log: 'addUserPreference',
    })

    const response = await axios.post(
      '/dashboard/userPreferences',
      preference,
      {
        headers: {
          'x-csrf-token': csrf,
        },
      },
    )

    endCPL({
      url: '/dashboard/userPreferences',
      log: 'addUserPreference',
      status: response.status,
    })
  } catch (error) {
    endCPL({
      url: '/dashboard/userPreferences',
      log: 'addUserPreference',
      status: error.response.status,
      error,
    })
  }
}

apiFactory.apiCredGenerateSecret = async (csrf, data) => {
  let response
  try {
    startCPL({
      url: '/dashboard/api/applications/createsecret',
      log: 'apiCredGenerateSecret',
    })
    response = await axios.post(
      '/dashboard/api/applications/createsecret',
      data,
      {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'x-csrf-token': csrf,
        },
      },
    )
    endCPL({
      url: '/dashboard/api/applications/createsecret',
      log: 'apiCredGenerateSecret',
      status: response.status,
    })
  } catch (error) {
    endCPL({
      url: '/dashboard/api/applications/createsecret',
      log: 'apiCredGenerateSecret',
      status: error.response.status,
      error,
    })
  }
  return response
}

apiFactory.apiCredDeleteSecret = async (csrf, info) => {
  let response
  try {
    startCPL({
      url: '/dashboard/api/applications/deletesecret',
      log: 'apiCredDeleteSecret',
    })
    const configs = {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'x-csrf-token': csrf,
      },
      data: info,
      responseType: 'json',
      contentType: 'application/json',
    }
    response = await axios.delete(
      `/dashboard/api/applications/deletesecret`,
      configs,
    )

    endCPL({
      url: '/dashboard/api/applications/deletesecret',
      log: 'apiCredDeleteSecret',
      status: response.status,
    })
  } catch (err) {
    endCPL({
      url: '/dashboard/api/applications/deletesecret',
      log: 'apiCredDeleteSecret',
      status: 500,
      err,
    })
  }
  return response
}

apiFactory.apiCredToggleSecret = async (csrf, data) => {
  let response
  try {
    startCPL({
      url: '/dashboard/api/applications/updatesecret',
      log: 'apiCredToggleSecretKeyStatus',
    })
    response = await axios.patch(
      '/dashboard/api/applications/updatesecret',
      data,
      {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'x-csrf-token': csrf,
        },
      },
    )
    endCPL({
      url: '/dashboard/api/applications/updatesecret',
      log: 'apiCredToggleSecretKeyStatus',
      status: response.status,
    })
  } catch (error) {
    endCPL({
      url: '/dashboard/api/applications/updatesecret',
      log: 'apiCredToggleSecretKeyStatus',
      status: error.response.status,
      error,
    })
  }
  return response
}

apiFactory.sendUnauthEmail = async (csrf) => {
  let response
  try {
    startCPL({
      url: '/dashboard/api/sendAdminEmail',
      log: 'sendEmailForUnauthUser',
    })
    response = await axios.post(
      '/dashboard/api/sendAdminEmail',
      {},
      {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'x-csrf-token': csrf,
        },
      },
    )
    endCPL({
      url: '/dashboard/api/sendAdminEmail',
      log: 'sendEmailForUnauthUser',
      status: response.status,
    })
  } catch (error) {
    endCPL({
      url: '/dashboard/api/sendAdminEmail',
      log: 'sendEmailForUnauthUser',
      status: error.response.status,
      error,
    })
    throw error
  }
  return response
}

export default apiFactory
